/**
 * `src/lib/ui/audio/player.ts` — the app's one recitation player.
 *
 * A module singleton, so playback survives navigation: the mini-player is in
 * the root layout and every route sees the same queue.
 */

import { createPlayer, type MediaSessionLike, type Player } from '$lib/audio/player';
import type { Track } from '$lib/audio/queue';
import { appFetch } from '$lib/privacy/fetch';
import { arabicIndic } from '$lib/ui/reader/numerals';
import { surahName } from '$lib/ui/reader/surahs';
import { readSetting, writeSetting } from '$lib/ui/theme/settings';

/** `سورة البقرة — الآية ٢٥٥`, or `… — البسملة` for the lead-in. */
export function trackLabel(track: Track): string {
	const where = `سورة ${surahName(track.surah)}`;
	return track.basmala ? `${where} — البسملة` : `${where} — الآية ${arabicIndic(track.ayah)}`;
}

let shared: Player | null = null;

export function recitationPlayer(): Player {
	if (shared) return shared;
	const nav = typeof navigator === 'undefined' ? null : navigator;
	const session = (
		nav && 'mediaSession' in nav ? nav.mediaSession : null
	) as MediaSessionLike | null;
	const Metadata = (globalThis as { MediaMetadata?: new (init: object) => unknown }).MediaMetadata;
	shared = createPlayer({
		createAudio: () => new Audio(),
		fetch: (url, init) => appFetch(url, init),
		mediaSession: session,
		metadata: Metadata ? (init) => new Metadata(init) : undefined,
		describe: trackLabel,
		loadReciter: () => readSetting('audio.reciter_id'),
		saveReciter: (id) => writeSetting('audio.reciter_id', id)
	});
	return shared;
}
